import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Injectable, Inject } from '@angular/core';
import { __awaiter } from 'tslib';
import { first } from 'rxjs/operators';
import * as i2 from '@angular/common/http';
import { Observable } from 'rxjs';
import { compact } from 'lodash';

const WINDOW = new InjectionToken('Window');

class SeFeAuthSupportModule {
    static forRoot(environment) {
        return {
            ngModule: SeFeAuthSupportModule,
            providers: [
                {
                    provide: 'env',
                    useValue: environment,
                },
            ],
        };
    }
}
SeFeAuthSupportModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAuthSupportModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeAuthSupportModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAuthSupportModule });
SeFeAuthSupportModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAuthSupportModule, providers: [{ provide: WINDOW, useValue: window }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeAuthSupportModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [{ provide: WINDOW, useValue: window }],
                }]
        }] });

class OAuthState {
    constructor(data) {
        if (data.returnTo) {
            this.returnTo = data.returnTo;
        }
        if (data.state) {
            this.state = data.state;
        }
    }
    static load() {
        const item = window.localStorage.getItem(OAuthState.KEY);
        let data = {};
        if (item) {
            data = JSON.parse(item);
        }
        window.localStorage.removeItem(OAuthState.KEY);
        return new OAuthState(data);
    }
    get state() {
        if (!this.internalState) {
            this.internalState = this.generateRandomStateValue();
        }
        return this.internalState;
    }
    set state(v) {
        this.internalState = v;
    }
    save() {
        const dataStr = JSON.stringify({
            state: this.state,
            returnTo: this.returnTo,
        });
        window.localStorage.setItem(OAuthState.KEY, dataStr);
    }
    generateRandomStateValue() {
        return (Math.random().toString(36).substring(2) +
            Math.random().toString(36).substring(2));
    }
}
OAuthState.KEY = 'OAuthState';

function isParamMap(arg) {
    return arg && arg.keys !== undefined;
}
class QSParamMap {
    constructor(params) {
        this.params = params;
    }
    has(name) {
        return this.params[name] !== undefined;
    }
    get(name) {
        let val = this.params[name];
        if (Array.isArray(val)) {
            val = val[0];
        }
        return val ? val.toString() : val;
    }
    getAll(name) {
        let val = this.params[name];
        if (!val) {
            val = [];
        }
        else if (!Array.isArray(val)) {
            val = [val];
        }
        return val.map((el) => el.toString());
    }
    get keys() {
        return this.params ? Object.keys(this.params) : [];
    }
}
class QueryString {
    static build(params) {
        const map = isParamMap(params) ? params : new QSParamMap(params);
        const pairs = [];
        map.keys.forEach((key) => {
            map.getAll(key).forEach((value) => {
                pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
            });
        });
        return pairs.join('&');
    }
    static paramMap(params) {
        return new QSParamMap(params);
    }
}

class User {
    constructor(data) {
        this.update(data);
    }
    isAuthenticated() {
        return !!this.id;
    }
    hasRole(roleAssignment) {
        const roleAssignments = this.role_assignments || [];
        if (typeof roleAssignment === 'string') {
            roleAssignment = { role: roleAssignment };
        }
        // Compare role assignments to the target role assignment, stopping if we find a match
        roleAssignmentCheck: for (let r = 0; r < roleAssignments.length; r++) {
            for (const prop in roleAssignment) {
                if (roleAssignment[prop] !== roleAssignments[r][prop]) {
                    continue roleAssignmentCheck;
                }
            }
            return true;
        }
        return false;
    }
    update(data) {
        Object.assign(this, data);
    }
}

class SeEnvironmentUrlService {
    constructor(env, windowRef) {
        this.env = env;
        this.windowRef = windowRef;
        this._window = windowRef;
    }
    /**
     * This determines the correct api url to use depending on the current window.location.hostname
     * and the options passed into se-fe-auth-support via the environment file.
     * Assume the returned string has a trailing /
     *
     * Will return undefined if no matches are found and no default is listed
     */
    getApiUrl() {
        const domain = this.getFromDomainUrlList(this.env.domainSpecificUrls);
        if (!domain) {
            console.error(`Error: No api url found for ${this._window.location.hostname} and no default found. Check the implementation of this apps environment.ts`);
            return undefined;
        }
        return this.ensureTrailingSlash(domain.api);
    }
    /**
     * This determines the correct user service url to use depending on the current window.location.hostname
     * and the options passed into se-fe-auth-support via the environment file.
     * Assume the returned string has a trailing /
     *
     * Will return undefined if no matches are found and no default is listed
     */
    getUserServiceUrl() {
        const domain = this.getFromDomainUrlList(this.env.domainSpecificUrls);
        if (!domain) {
            console.error(`Error: No user url found for ${this._window.location.hostname} and no default found. Check the implementation of this apps environment.ts`);
            return undefined;
        }
        return this.ensureTrailingSlash(domain.user);
    }
    getFromDomainUrlList(list) {
        const found = list.find(data => {
            return this._window.location.hostname.toLowerCase().endsWith(data.domain.toLowerCase());
        });
        const fallback = list.find(data => data.default);
        return found || fallback;
    }
    /**
     * This determines the correct oath/callback url to use depending on the current window.location.hostname
     * and the options passed into se-fe-auth-support via the environment file.
     *
     * Will return default with base of 'window.location.origin' if not specified in the env config.
     */
    getOauthCallbackUri() {
        const domain = this.getFromDomainUrlList(this.env.domainSpecificUrls);
        if (!domain || !domain.oauthCallbackUri) {
            return `${window.location.origin}/oauth/callback`;
        }
        return domain.oauthCallbackUri;
    }
    ensureTrailingSlash(url) {
        return url.endsWith('/') ? url : `${url}/`;
    }
}
SeEnvironmentUrlService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeEnvironmentUrlService, deps: [{ token: 'env' }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
SeEnvironmentUrlService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeEnvironmentUrlService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeEnvironmentUrlService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['env']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class SeAuthService {
    constructor(env, urlService, http, windowRef) {
        this.env = env;
        this.urlService = urlService;
        this.http = http;
        this.windowRef = windowRef;
        this._window = windowRef;
        this.userAuthenticatedResult = new Promise((resolve) => {
            this.getUser()
                .pipe(first())
                .subscribe((data) => {
                var _a, _b;
                this.user = new User((_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.user);
                if ((_b = data === null || data === void 0 ? void 0 : data.result) === null || _b === void 0 ? void 0 : _b.user) {
                    resolve(this.user);
                }
            }, (error) => {
                this.user = new User({});
                resolve(this.user);
            });
        });
    }
    me() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.user) {
                yield this.userAuthenticatedResult;
            }
            return this.user;
        });
    }
    isAuthenticated() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.user) {
                yield this.userAuthenticatedResult;
            }
            return this.user.isAuthenticated();
        });
    }
    isAdmin() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.user) {
                yield this.userAuthenticatedResult;
            }
            return (this.user.hasRole('platform_admin') || this.user.hasRole('third_north'));
        });
    }
    /**
     * This async method will resolve to true if the resource role is present for the user.
  
     * For a complete list of resource and role types, see: https://github.com/sportngin/user_service/blob/master/app/models/role_assignment.rb
  
     * Examples:
  
     *   // Global role (matches any system or resource)
     *   authService.hasRole('the_role')
  
     *   // Role on a resource type (across systems)
     *   authService.hasRole({
     *     role: 'the_role',
     *     resource_type: 'ResourceType'
     *   })
  
     *   // Role on a specific resource
     *   authService.hasRole({
     *     role: 'the_role',
     *     resource_system: 'SystemName',
     *     resource_type: 'ResourceType',
     *     resource_id: 'abc123'
     *   })
     */
    hasRole(role) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.user) {
                yield this.userAuthenticatedResult;
            }
            return this.user.hasRole(role);
        });
    }
    /**
     * This async method will resolve to true if any of the resource roles is present for the user.
     * @param roles
     */
    hasAnyRole(roles) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.user) {
                yield this.userAuthenticatedResult;
            }
            return roles.some(role => this.user.hasRole(role));
        });
    }
    initiateLogin(returnURL) {
        const state = new OAuthState({ returnTo: returnURL });
        state.save();
        this._window.location.href = this.redirectUrl(state.state);
    }
    uri(path) {
        return `${this.urlService.getApiUrl()}${encodeURI(path)}`;
    }
    getUser() {
        return this.get('oauth/me');
    }
    get(path, options) {
        return this.http.get(this.uri(path), this.getOptions(options));
    }
    getOptions(options) {
        options = options || {};
        options.withCredentials = true;
        options.headers = options.headers || {};
        options.headers.Accept = options.headers.Accept || 'application/json';
        return options;
    }
    redirectUrl(state) {
        const queryParams = {
            client_id: this.env.clientId,
            redirect_uri: this.urlService.getOauthCallbackUri(),
            response_type: 'none',
            state,
        };
        return `${this.urlService.getUserServiceUrl()}oauth/authorize?${QueryString.build(queryParams)}`;
    }
}
SeAuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAuthService, deps: [{ token: 'env' }, { token: SeEnvironmentUrlService }, { token: i2.HttpClient }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
SeAuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['env']
                }] }, { type: SeEnvironmentUrlService }, { type: i2.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class SeAuthGuardService {
    constructor(authService) {
        this.authService = authService;
    }
    canActivate(route, state) {
        const returnURL = this.makeURL(route);
        return new Observable((observer) => {
            this.authService
                .isAuthenticated()
                .then((authenticated) => {
                observer.next(authenticated);
                observer.complete();
                if (!authenticated) {
                    this.authService.initiateLogin(returnURL);
                }
            })
                .catch((e) => this.authService.initiateLogin(returnURL));
        });
    }
    canActivateChild(route, state) {
        return this.canActivate(route, state);
    }
    makeURL(route) {
        const qs = QueryString.build(route.queryParamMap);
        const path = `/${this.getDescendentPath(route)}`;
        return qs.length === 0 ? path : `${path}?${qs}`;
    }
    getDescendentPath(route) {
        var _a;
        if (!((_a = route.children) === null || _a === void 0 ? void 0 : _a.length))
            return route.url.map(seg => seg.path).join('/');
        return compact([route.url.map(seg => seg.path).join('/'), route.children.map(child => this.getDescendentPath(child)).join('/')]).join('/');
    }
}
SeAuthGuardService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAuthGuardService, deps: [{ token: SeAuthService }], target: i0.ɵɵFactoryTarget.Injectable });
SeAuthGuardService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAuthGuardService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAuthGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SeAuthService }]; } });

class SeAdminGuardService {
    constructor(authService, authGuard, env) {
        this.authService = authService;
        this.authGuard = authGuard;
        this.env = env;
    }
    canActivate(route, state) {
        return new Observable((observer) => {
            this.authService.isAdmin().then((result) => {
                observer.next(result);
                observer.complete();
                if (!result) {
                    window.location.href = this.env.defaultUrl;
                }
            });
        });
    }
    canActivateChild(route, state) {
        return this.canActivate(route, state);
    }
}
SeAdminGuardService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAdminGuardService, deps: [{ token: SeAuthService }, { token: SeAuthGuardService }, { token: 'env' }], target: i0.ɵɵFactoryTarget.Injectable });
SeAdminGuardService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAdminGuardService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeAdminGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SeAuthService }, { type: SeAuthGuardService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['env']
                }] }]; } });

/*
 * Public API Surface of se-fe-auth-support
 */

/**
 * Generated bundle index. Do not edit.
 */

export { OAuthState, SeAdminGuardService, SeAuthGuardService, SeAuthService, SeEnvironmentUrlService, SeFeAuthSupportModule };
